"use client";

import type { ButtonHTMLAttributes, HTMLAttributes, MouseEvent } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import {
  type ActiveProp,
  type BorderProp,
  type ChildrenProp,
  type ClassNameProp,
  type EdgesProps,
  type TrackingProps,
  polymorphicForwardRef,
} from "../../props";
import { Edges } from "../Edges";
import { Text, type TextProps } from "../Text";
import { useTracker } from "../../hooks/useTracker";

import {
  linkButtonContainerStyle,
  linkButtonStyle,
  linkButtonTextStyle,
} from "./LinkButton.css";
import { focusVisibleInset } from "../../theme/css/focusVisible.css";

export type LinkButtonProps<
  T extends HTMLAttributes<Element> = ButtonHTMLAttributes<HTMLButtonElement>,
> = T &
  ChildrenProp &
  ClassNameProp &
  BorderProp &
  ActiveProp &
  TrackingProps &
  RecipeVariants<typeof linkButtonContainerStyle> &
  EdgesProps & {
    align?: TextProps["align"];
  };

/**
 * Button component.
 */
export const LinkButton = polymorphicForwardRef<"button", LinkButtonProps>(
  (
    {
      as: Element = "button",
      variant = "accent",
      trailing,
      active = false,
      leading,
      tracking,
      align,
      alignSelf,
      justifySelf,
      className,
      children,
      onClick,
      ...props
    },
    forwardedRef,
  ) => {
    const { track } = useTracker();

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (tracking) {
        track("interaction", {
          category: "button",
          action: "click",
          label: tracking,
          properties: {
            buttonId: tracking,
            buttonText: typeof children === "string" ? children : undefined,
            buttonType: "link",
            buttonVariant: variant,
          },
        });
      }
    };

    return (
      <Element
        ref={forwardedRef}
        className={mergeClasses(
          linkButtonStyle,
          linkButtonContainerStyle({ alignSelf, justifySelf, variant }),
          className,
          focusVisibleInset,
        )}
        onClick={handleClick}
        data-tracking={tracking}
        {...props}
        {...(active ? { "data-active": "true" } : {})}
      >
        <Edges trailing={trailing} leading={leading} hideOverflow>
          {children && (
            <Text align={align} ellipsis className={linkButtonTextStyle}>
              {children}
            </Text>
          )}
        </Edges>
      </Element>
    );
  },
);

LinkButton.displayName = "LinkButton";
